/**
 * OcrSettingsPage — Okmanvolvasó & {t('ocr.labelTab')} beallitasok
 * Epic 70: Okmanvolvasó & Zebra Cimke
 *
 * Dokumentum beolvasas, Zebra/{t('ocr.generateBrother')} UI.
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useGenerateBrotherLabel, useGenerateZebraLabel, useScanDocument } from '@/hooks/useOcr';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function OcrSettingsPage() {
  const { t } = useTranslation();
  const scanMutation = useScanDocument();
  const zebraMutation = useGenerateZebraLabel();
  const brotherMutation = useGenerateBrotherLabel();

  // OCR state
  const [scanResult, setScanResult] = useState<{
    name: string;
    documentNumber: string;
    confidence: number;
  } | null>(null);

  // Label state
  const [zebraForm, setZebraForm] = useState({
    productName: '',
    barcode: '',
    price: '',
    sku: '',
  });

  const [brotherForm, setBrotherForm] = useState({
    productName: '',
    price: '',
    vatRate: '27',
    barcode: '',
    category: '',
  });

  const [zebraResult, setZebraResult] = useState<string | null>(null);
  const [brotherResult, setBrotherResult] = useState<string | null>(null);

  const handleScan = async () => {
    // Legacy stub: the browser-side OCR pipeline (70-1B Tesseract) replaces this.
    // This page is scheduled for removal in a follow-up frontend cleanup story.
    const result = await scanMutation.mutateAsync('placeholder-image-data');
    setScanResult({
      name: result.data.ocrResult.name,
      documentNumber: result.data.ocrResult.documentNumber,
      confidence: result.data.ocrResult.confidence,
    });
  };

  const handleZebraLabel = async () => {
    const price = parseInt(zebraForm.price, 10);
    if (!zebraForm.productName || !zebraForm.barcode || !price) return;

    const input: { productName: string; barcode: string; price: number; sku?: string } = {
      productName: zebraForm.productName,
      barcode: zebraForm.barcode,
      price,
    };
    if (zebraForm.sku) {
      input.sku = zebraForm.sku;
    }

    const result = await zebraMutation.mutateAsync(input);
    setZebraResult(result.data.zpl);
  };

  const handleBrotherLabel = async () => {
    const price = parseInt(brotherForm.price, 10);
    const vatRate = parseInt(brotherForm.vatRate, 10);
    if (!brotherForm.productName || !price) return;

    const input: {
      productName: string;
      price: number;
      vatRate: number;
      barcode?: string;
      category?: string;
    } = {
      productName: brotherForm.productName,
      price,
      vatRate,
    };
    if (brotherForm.barcode) {
      input.barcode = brotherForm.barcode;
    }
    if (brotherForm.category) {
      input.category = brotherForm.category;
    }

    const result = await brotherMutation.mutateAsync(input);
    setBrotherResult(
      `${result.data.productName}: ${formatCurrency(result.data.grossPrice)} brutto`
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('ocr.pageTitle')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* OCR Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('ocr.scanTab')} (IPEVO V4K)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{t('ocr.scanInstruction')}</p>
            <Button onClick={handleScan} disabled={scanMutation.isPending}>
              {scanMutation.isPending ? t('ocr.scanning') : t('ocr.scanButtonLabel')}
            </Button>

            {scanMutation.isError && (
              <p className="text-red-500 text-sm">
                {t('common.errorLabel')}: {scanMutation.error?.message ?? t('ocr.errorUnknown')}
              </p>
            )}

            {scanResult && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg space-y-2">
                <p className="font-medium">{t('ocr.scanSuccess')}</p>
                <div className="text-sm space-y-1">
                  <p>
                    {t('ocr.nameLabel')}: {scanResult.name}
                  </p>
                  <p>
                    {t('ocr.documentNumberLabel')}: {scanResult.documentNumber}
                  </p>
                  <p>
                    {t('ocr.confidenceLabel')}: {scanResult.confidence}%
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zebra Label Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.ocr.zebraLabel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={t('ocr.productNamePlaceholder')}
              value={zebraForm.productName}
              onChange={e => setZebraForm({ ...zebraForm, productName: e.target.value })}
            />
            <Input
              placeholder={t('ocr.barcodePlaceholder')}
              value={zebraForm.barcode}
              onChange={e => setZebraForm({ ...zebraForm, barcode: e.target.value })}
            />
            <Input
              type="number"
              placeholder={t('ocr.pricePlaceholder')}
              value={zebraForm.price}
              onChange={e => setZebraForm({ ...zebraForm, price: e.target.value })}
            />
            <Input
              placeholder={t('ocr.skuPlaceholder')}
              value={zebraForm.sku}
              onChange={e => setZebraForm({ ...zebraForm, sku: e.target.value })}
            />
            <Button onClick={handleZebraLabel} disabled={zebraMutation.isPending}>
              {zebraMutation.isPending ? t('ocr.generating') : t('ocr.labelTitle')}
            </Button>

            {zebraResult && (
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                {zebraResult}
              </pre>
            )}
          </CardContent>
        </Card>

        {/* Brother Label Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('ocr.brotherLabel')} arcimke (50mm)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={t('ocr.productNamePlaceholder')}
              value={brotherForm.productName}
              onChange={e => setBrotherForm({ ...brotherForm, productName: e.target.value })}
            />
            <Input
              type="number"
              placeholder={t('ocr.netPricePlaceholder')}
              value={brotherForm.price}
              onChange={e => setBrotherForm({ ...brotherForm, price: e.target.value })}
            />
            <Input
              type="number"
              placeholder={t('ocr.vatRatePlaceholder')}
              value={brotherForm.vatRate}
              onChange={e => setBrotherForm({ ...brotherForm, vatRate: e.target.value })}
            />
            <Input
              placeholder={t('ocr.barcodeOptionalPlaceholder')}
              value={brotherForm.barcode}
              onChange={e => setBrotherForm({ ...brotherForm, barcode: e.target.value })}
            />
            <Input
              placeholder={t('ocr.categoryPlaceholder')}
              value={brotherForm.category}
              onChange={e => setBrotherForm({ ...brotherForm, category: e.target.value })}
            />
            <Button onClick={handleBrotherLabel} disabled={brotherMutation.isPending}>
              {brotherMutation.isPending ? t('ocr.generatingLabel') : t('ocr.priceLabelGenerate')}
            </Button>

            {brotherResult && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                {brotherResult}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
